import posthog from 'posthog-js'
import networkMonitor from './service/networkMonitor'
import sessionMonitor from './service/sessionMonitor'
import workosTokenMonitor from './service/workosTokenMonitor'
import graphState from './graph/state'
import useAuth from './models/useAuth'
import { getUserId } from './lib/workos'
import useNetwork from './models/useNetwork'
import useOrganizations from './models/useOrganizations'
import useBilling from './models/useBilling'
import useOrgMembers from './models/useOrgMembers'
import useProfile from './models/useProfile'
import useSession from './models/useSession'
import useToken from './models/useToken'
import useNodes from './models/useNode'
import useNodeMetadata from './models/useNodeMetadata'
import useSetPlaces from './models/useSetPlaces'
import useUpgrades from './models/useUpgrades'
import useServices from './models/useServices'
import useChannel from './models/useChannel'
import useApp from './models/useApp'
import useTerminal from './models/useTerminal'
import useUsage from './models/useUsage'
import useTasks from './models/useTasks'
import useNodeTasks from './models/useNodeTasks'
import useDeadlineNotify from './models/useDeadlineNotify'
import useFiles from './models/useFiles'
import { Session } from '@supabase/supabase-js'
import { singleFlight } from './utilities/singleFlight'

class Controller {
  initialized = false
  // Stores kept live by a realtime subscription. useOrganizations is first so org context (active
  // org, role) is loaded before networks/nodes — 1.3 scopes those stores by the active org.
  subscribed = [useOrganizations, useBilling, useProfile, useNodes, useServices, useNetwork, useSession, useToken, useChannel]
  // ...plus the stores that are loaded but NOT subscribed. `node_metadata` and `upgrades` are not
  // members of the supabase_realtime publication, so a channel on either joins and never delivers
  // an event (app#132); they are refreshed at load and on org switch instead. Every store here
  // still resets on sign-out, which is what this list is for.
  models = [...this.subscribed, useNodeMetadata, useSetPlaces, useUpgrades]
  subscriptions: (() => void)[] = []
  /** The `connect` listener `signIn` adds, kept so `signOut` can remove it. */
  private onConnect?: () => void

  async init() {
    try {
      const session = await useAuth.getState().initSession()
      if (session) {
        await this.signIn(session)
      }
    } catch (error) {
      console.error('[Controller] Failed to initialize auth:', error)
      useApp.setState({
        error: 'Authentication initialization failed. Please try refreshing the page.',
      })
    }
  }

  async signIn(session: Session) {
    if (this.initialized) return
    this.initialized = true
    await this.refresh()
    graphState.load()
    sessionMonitor.start()
    await workosTokenMonitor.start(async () => {
      await this.refresh()
    })
    useApp.setState({ ready: true })

    const userId = getUserId()
    if (userId) {
      posthog.identify(userId, { email: session.user.email })
    }

    // ⚠⚠ THE TOKEN FIRST, THEN THE DATA (app#349). After a sleep the access token has usually
    // expired, and a refresh that runs beside the token check reads every store and opens every
    // realtime channel with the old token. `check()` is shared with the monitor's own `connect`
    // listener, so this waits for the same check rather than starting a second one.
    // ⚠ One listener per sign-in: a second sign-in in the same tab used to add a second one.
    if (this.onConnect) networkMonitor.off('connect', this.onConnect)
    this.onConnect = () => {
      void (async () => {
        // ⚠⚠ READ THE ANSWER. `check()` resolves false when it could not establish the token —
        // a status fetch that failed because DNS is still cold on a wake is the common case — and
        // reading the stores then is exactly the expired-token read app#349 is about. The monitor
        // owes the reload from there: its retry runs `onRecovered`, which is this refresh.
        const before = useApp.getState().refreshedAt
        const ready = await workosTokenMonitor.check()
        if (!this.initialized || !ready) return
        // ⚠ The check may have reloaded already, through `onRecovered`. Two sequential reloads are
        // not coalesced by `singleFlight`, so without this every recovery wake re-reads every
        // store and re-opens every realtime channel twice.
        if (useApp.getState().refreshedAt !== before) return
        await this.refresh()
      })().catch(error => console.error('[Controller] wake refresh failed', error))
    }
    networkMonitor.on('connect', this.onConnect)
  }

  /**
   * Load every store again and re-open the realtime channels.
   *
   * ⚠⚠ ONE AT A TIME (app#349). A wake can ask for a refresh twice — the `connect` listener and
   * the token monitor's `onRecovered` — and two overlapping runs each call `subscribeAll`, which
   * pushes a second set of channels that no `unsubscribeAll` ever closes. `singleFlight` runs them
   * in turn, and a caller that asks during a run waits for a run that started after it asked.
   */
  refresh = singleFlight(async () => {
    this.unsubscribeAll()
    await this.loadState()
    this.subscribeAll()
    useApp.setState({ loaded: true, refreshedAt: Date.now() })
  })

  // Load stores by data dependency, not by lock-step tiers. Order still matters:
  //  0. useOrganizations sets activeOrgId (networks/nodes/billing scope by it).
  //  1. everything that needs only activeOrgId, or nothing — all in parallel.
  //  2. useNodeMetadata/useServices/useUpgrades derive ONLY from useNode's nodes,
  //     so they start the moment nodes resolves rather than waiting for the whole
  //     tier-1 barrier. Without this, a slow independent sibling (e.g. the
  //     app_latest_sessions view, which can spike to several seconds when it
  //     catches a cold pooled connection) gated services behind it —
  //     they appeared ~6.5s in instead of ~2s though nodes was ready at ~2s.
  // Membership must stay in sync with the `models` array above (used by reset) and with
  // useOrganizations.setActiveOrg's node-derived batch, which re-scopes exactly this set on an org
  // switch. A get() that rejects rather than swallowing its error would reject the whole batch, so
  // each store's get() must keep catching internally.
  async loadState() {
    await useOrganizations.getState().get()
    const nodes = useNodes.getState().get()
    const nodeDerived = nodes.then(() =>
      Promise.all([useNodeMetadata, useSetPlaces, useServices, useUpgrades].map(m => m.getState().get()))
    )
    await Promise.all([
      useBilling.getState().get(),
      useProfile.getState().get(),
      nodes,
      useNetwork.getState().get(),
      useSession.getState().get(),
      useToken.getState().get(),
      useChannel.getState().get(),
      nodeDerived,
    ])
  }

  subscribeAll() {
    for (const model of this.subscribed) {
      this.subscriptions.push(model.getState().subscribe())
    }
  }

  unsubscribeAll() {
    this.subscriptions.forEach(unsubscribe => unsubscribe())
    this.subscriptions = []
  }

  async signOut() {
    console.log('controller sign out')
    this.initialized = false
    sessionMonitor.stop()
    workosTokenMonitor.stop()
    if (this.onConnect) {
      networkMonitor.off('connect', this.onConnect)
      this.onConnect = undefined
    }
    useAuth.getState().reset()
    useAuth.getState().setInitialized(true)
    useApp.setState({ ready: false, loaded: false })
    for (const model of this.models) {
      await model.getState().reset()
    }
    // Loaded per-active-org by useOrganizations (not in the models array); clear it
    // on sign-out so a previous user's org roster doesn't linger in memory.
    useOrgMembers.getState().reset()
    useTerminal.getState().reset()
    useUsage.getState().reset()
    // Loaded on demand by the usage screen, like useUsage above — not in `models`, so it needs
    // its own reset. It holds the previous user's MACHINE NAMES, which must not survive a
    // sign-in as somebody else in the same tab.
    useTasks.getState().reset()
    // The compute node page's task rows (app#330), loaded on demand like useTasks.
    useNodeTasks.getState().reset()
    // The compute time limit email preference (nobgp/app#354), loaded on demand like useTasks.
    useDeadlineNotify.getState().reset()
    // ⚠ Cached WebDAV listings, and they are NOT the same as the org roster
    // above: since the node tree landed these include a node's PRIVATE area,
    // which is gated at Owner/Admin in the router precisely because nothing
    // else stands in front of it. A sign-in as a different user in the same tab
    // would otherwise render the previous user's file names out of this store,
    // before any request is made — the store already has `reset`, nothing was
    // calling it, and it is not in `this.models`.
    useFiles.getState().reset()
    graphState.reset()
  }
}

export default new Controller()
